import { workspaceTools } from "./workspace-tools.js";
import { S, ui } from "../../app/store.js";
import { daysTo, fmtD, when } from "../../domain/dates.js";
import { PERMS } from "../../domain/reference.js";
import { V } from "../../app/pages.js";
import { can, emp, me, uname } from "../../services/access.js";
import { chip, csvBtn, panel, seg, tile } from "../../components/ui.js";
import { esc, pad2 } from "../../domain/format.js";
import { authenticatorAppInfo } from "../../components/account-setup-guide.js";

const ADM_TABS = [
  ["overview", "Overview"],
  ["users", "Users"],
  ["roles", "Roles & permissions"],
  ["company", "Company & books"],
  ["security", "Security"],
  ["audit", "Audit log"],
  ["data", "Data & privacy"],
];

const ADM_SUB = {
  overview:
    "The health of Milltech One: who can get in, what is protected, and what needs an administrator.",
  users:
    "Create user profiles, manage access, and let each user connect their own authenticator.",
  roles: "What each role may do. Conflicting rights in one role are flagged.",
  company:
    "Registration, period locks, approval limits and the invoice series.",
  security: "Sign-in policy and the sign-in history.",
  audit: "Every action in Milltech One, append-only.",
  data: "Backups, exports and the Data Privacy Act duties.",
};

const SOD = [
  [["pay.prepare", "pay.approve"], "Prepares and approves the same payroll"],
  [
    ["admin.users", "gl.approve"],
    "Can grant itself rights and approve postings",
  ],
  [
    ["admin.users", "pay.approve"],
    "Can grant itself rights and approve payroll",
  ],
  [
    ["sys.admin", "gl.approve"],
    "System administration combined with posting approval",
  ],
  [
    ["sys.admin", "pay.release"],
    "System administration combined with releasing pay",
  ],
  [
    ["inv.issue", "period.lock"],
    "Issues invoices and controls the period lock",
  ],
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const activeOf = (role) =>
  S.users.filter((u) => u.role === role && u.status === "ACTIVE").length;

function guardChange(u, role, status) {
  if (u.id === ui.user)
    return "You can't change your own access. Ask another administrator.";
  for (const r of ["SUPER", "OWNER"])
    if (
      u.role === r &&
      u.status === "ACTIVE" &&
      (role !== r || status !== "ACTIVE") &&
      activeOf(r) <= 1
    )
      return `${u.name} is the only active ${S.roles[r].name}. Add another before changing this account.`;
  return "";
}

function sodConflicts() {
  const out = [];
  for (const [k, r] of Object.entries(S.roles)) {
    if (k === "OWNER" || k === "SUPER") continue;
    for (const [ps, why] of SOD)
      if (ps.every((p) => r.perms.includes(p))) out.push({ role: k, why, ps });
  }
  return out;
}

function adminAlerts() {
  const a = [],
    st = S.settings;
  if (st.mfaRequired)
    S.users
      .filter((u) => u.status === "ACTIVE" && !u.mfa)
      .forEach((u) =>
        a.push({
          t: `${u.name} has no two-factor sign-in`,
          s: "Policy requires it for every account",
          tone: "bad",
          act: "u-mfa",
          id: u.id,
          label: "Enrollment Instructions",
        }),
      );
  S.users
    .filter(
      (u) => u.expires && u.status === "ACTIVE" && daysTo(u.expires) <= 60,
    )
    .forEach((u) =>
      a.push({
        t: `${u.name}'s access ends ${fmtD(u.expires)}`,
        s: "Extend it in Users if the engagement continues",
        tone: "warn",
      }),
    );
  S.users
    .filter((u) => u.status === "INVITED")
    .forEach((u) =>
      a.push({
        t: u.invitationStatus
          ? `${u.name} has a pending invitation`
          : `${u.name} needs an invitation`,
        s: u.invitationStatus
          ? `Invitation sent to ${u.email}`
          : `Send a setup link to ${u.email}`,
        tone: "warn",
        act: "u-resend",
        id: u.id,
        label: u.invitationStatus ? "Resend Invitation" : "Send Invitation",
      }),
    );
  sodConflicts().forEach((c) =>
    a.push({
      t: `${S.roles[c.role].name}: ${c.why}`,
      s: c.ps.map((p) => PERMS[p]).join(" + "),
      tone: "bad",
      go: "roles",
    }),
  );
  if ((Date.now() - Date.parse(st.backupAt)) / 36e5 > 26)
    a.push({
      t: "The last backup is more than a day old",
      s: `Last run ${new Date(st.backupAt).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}`,
      tone: "bad",
      act: "backup",
      label: "Run backup",
    });
  return a;
}

V.admin = () => {
  const sys = can("sys.admin"),
    tabs = ADM_TABS.filter(([v]) => sys || ["users", "roles"].includes(v)),
    tab = tabs.some((t) => t[0] === ui.adm) ? ui.adm : tabs[0][0];
  const body = {
    overview: admOverview,
    users: admUsers,
    roles: admRoles,
    company: admCompany,
    security: admSecurity,
    audit: admAudit,
    data: admData,
  }[tab]();
  return `<div class="top" style="margin-top:-12px"><span class="muted">${ADM_SUB[tab]}</span><div class="row">${chip(S.roles[me().role].name, "acc")}</div></div>
  <div class="vtabs" role="tablist"><div>${tabs.map(([v, l]) => `<button role="tab" aria-selected="${v === tab}" data-seg="adm" data-val="${v}">${l}</button>`).join("")}</div></div>${body}`;
};

function admOverview() {
  const act = S.users.filter((u) => u.status === "ACTIVE"),
    mfa = act.filter((u) => u.mfa).length,
    fail = S.signins.filter(
      (x) => !x.ok && Date.now() - Date.parse(x.t) < 7 * 864e5,
    ).length,
    al = adminAlerts();
  const recent = S.audit
    .filter((x) =>
      /role|permission|user|invit|suspend|reactivat|two-factor|policy|lock|reopen|backup|export|registration|limit|sign out/i.test(
        x.a,
      ),
    )
    .slice(-6)
    .reverse();
  return `<div class="tiles">${tile("Active users", pad2(act.length), "g")}${tile("Accounts Pending Setup", pad2(S.users.filter((u) => u.status === "INVITED").length), S.users.some((u) => u.status === "INVITED") ? "o" : "g")}${tile("Two-factor coverage", Math.round((mfa / act.length) * 100) + "%", mfa === act.length ? "g" : "r")}${tile("Failed sign-ins, 7 days", pad2(fail), fail > 2 ? "r" : fail ? "o" : "g")}${tile("Books locked through", fmtD(S.lockedThrough), "g")}</div>
  <div class="grid"><section class="panel"><div class="panel-h"><h2>Needs an administrator</h2>${al.length ? chip(`${al.length} open`, "bad") : chip("All clear", "good")}</div>
   <div class="inbox">${al.map((x) => `<div><div><strong>${esc(x.t)}</strong><div class="small muted">${esc(x.s)}</div></div><div class="row">${x.act ? `<button data-act="${x.act}" data-id="${x.id || ""}">${x.label}</button>` : ""}${x.go ? `<button class="ghost" data-seg="adm" data-val="${x.go}">Review</button>` : ""}</div></div>`).join("") || `<div><span class="muted">Nothing needs an administrator.</span></div>`}</div></section>
   <section class="panel"><div class="panel-h"><h2>System</h2></div><div class="inbox">
    ${[
      [
        "Database",
        "Demo data in this browser. Production: Supabase Postgres with row-level security",
        "Demo",
        "mut",
      ],
      [
        "Backups",
        `Last run ${when(S.settings.backupAt)}`,
        "On request",
        "good",
      ],
      [
        "Sign-in",
        S.settings.mfaRequired
          ? "Two-factor required for every account"
          : "Two-factor optional",
        S.settings.mfaRequired ? "Enforced" : "Optional",
        S.settings.mfaRequired ? "good" : "warn",
      ],
      [
        "BIR EIS",
        "Electronic invoicing link for the sales invoices",
        "Not connected",
        "warn",
      ],
      ["Email", "Invitations and payslip notices", "Not connected", "warn"],
    ]
      .map(
        (r) =>
          `<div><div><strong>${r[0]}</strong><div class="small muted">${r[1]}</div></div>${chip(r[2], r[3])}</div>`,
      )
      .join("")}</div></section></div>
  <section class="panel"><div class="panel-h"><h2>Recent administration</h2><button class="ghost link" data-seg="adm" data-val="audit">Full audit log</button></div><div class="inbox">${recent.map((x) => `<div><div><strong>${esc(x.a)}</strong><div class="small muted">${esc(x.d)}</div></div><span class="small muted">${esc(uname(x.u))} · ${when(x.t)}</span></div>`).join("") || `<div><span class="muted">No administrative changes yet.</span></div>`}</div></section>`;
}

function admUsers() {
  const roles = Object.entries(S.roles),
    f = ui.uf || "all",
    list = S.users.filter((u) => f === "all" || u.status === f);
  return `${
    ui.invUser
      ? `<form class="panel" id="userform"><div class="panel-h"><div><h2>Add a user</h2><span class="muted">Create the profile, then send an invitation email below the user list.</span></div></div><div class="form">
    <label>Full name<input id="nu-name" value="${esc(ui.invUser.name || "")}"></label><label>Work email<input id="nu-email" type="email" value="${esc(ui.invUser.email || "")}" placeholder="name@milltech.example"></label>
    <label>Role<select id="nu-role">${roles.map(([k, r]) => `<option value="${k}"${k === (ui.invUser.role || "EMP") ? " selected" : ""}>${esc(r.name)}</option>`).join("")}</select></label>
    <label>Linked employee<select id="nu-emp"><option value="">None (external or admin)</option>${S.employees
      .filter((e) => !S.users.some((u) => u.emp === e.id))
      .map(
        (e) =>
          `<option value="${e.id}">${esc(e.name)} · ${esc(e.position)}</option>`,
      )
      .join("")}</select></label>
    <label>Access ends (optional)<input id="nu-exp" type="date" value="${esc(ui.invUser.expires || "")}"></label></div>
    ${ui.uErr ? `<p class="err">${esc(ui.uErr)}</p>` : ""}<div class="row"><button class="pri" type="submit">Create User Profile</button><button type="button" class="ghost" data-act="u-cancel">Cancel</button></div></form>`
      : ""
  }
  ${panel(
    "Users",
    `${S.users.filter((u) => u.status === "ACTIVE").length} active · ${S.users.length} accounts`,
    `${ui.invUser ? "" : `<button class="pri" data-act="u-invite">Add user</button>`}${csvBtn("t-users")}`,
    `${seg(
      "uf",
      [
        ["all", "All"],
        ["ACTIVE", "Active"],
        ["INVITED", "Pending Setup"],
        ["SUSPENDED", "Suspended"],
      ],
      f,
    )}
   <div class="tbl"><table id="t-users"><thead><tr><th>User</th><th>Role</th><th>Status</th><th>Two-factor</th><th>Authenticator app</th><th>Last sign-in</th><th>Access ends</th><th></th></tr></thead><tbody>
   ${list
     .map((u) => {
       const self = u.id === ui.user;
       const authenticatorApp = authenticatorAppInfo(u.authenticatorApp);
       const invitation = u.invitationStatus;
       const expiredInvite =
         invitation?.expiresAt &&
         new Date(invitation.expiresAt).getTime() <= Date.now();
       return `<tr><td><strong>${esc(u.name)}</strong>${self ? ` <span class="small muted">(you)</span>` : ""}<div class="small muted">${esc(u.email)}${u.emp ? ` · ${esc(emp(u.emp).position)}` : u.title ? ` · ${esc(u.title)}` : ""}</div></td>
    <td><select id="role-${u.id}" data-act="role" data-id="${u.id}" ${self ? "disabled" : ""} aria-label="Role of ${esc(u.name)}">${roles.map(([k, r]) => `<option value="${k}"${k === u.role ? " selected" : ""}>${esc(r.name)}</option>`).join("")}</select></td>
    <td>${u.status === "SUSPENDED" ? chip("Suspended", "bad") : !u.signInEnabled ? chip(invitation ? (expiredInvite ? "Invitation Expired" : "Invitation Sent") : "Pending Setup", "warn") : chip("Active", "good")}${invitation?.sentAt && !u.signInEnabled ? `<div class="small muted">Sent ${when(invitation.sentAt)}</div>` : ""}</td><td>${u.mfa ? chip("On", "good") : chip("Off", S.settings.mfaRequired ? "bad" : "mut")}</td><td>${u.mfa ? authenticatorApp ? `<span class="auth-app-cell"><img src="${esc(authenticatorApp.logo)}" width="20" height="20" alt="">${esc(authenticatorApp.name)}</span>` : '<span class="small muted">Not recorded</span>' : '<span class="small muted">Not set up</span>'}</td>
    <td class="small" style="white-space:nowrap">${u.lastSeen ? new Date(u.lastSeen).toLocaleString("en-PH", { timeZone: "Asia/Manila", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : u.signInEnabled ? "Not Recorded" : "Never"}</td><td>${u.expires ? fmtD(u.expires) : "—"}</td>
    <td><div class="row" style="flex-wrap:nowrap">${self ? "" : u.status === "SUSPENDED" ? `<button data-act="u-reactivate" data-id="${u.id}">Reactivate</button>` : !u.signInEnabled ? `<button data-act="u-resend" data-id="${u.id}">${invitation ? "Resend Invitation" : "Send Invitation"}</button><button class="ghost" data-act="u-suspend" data-id="${u.id}">Revoke</button>` : `<span class="small muted">${u.mfa ? "Authenticator Enabled" : "Enroll At Sign-In"}</span><button class="ghost" data-act="u-signout" data-id="${u.id}">Sign out</button><button class="ghost" data-act="u-suspend" data-id="${u.id}">Suspend</button>`}</div></td></tr>`;
     })
     .join("")}
   </tbody></table></div><p class="small muted">The app name is selected or reported by each user; verification codes do not identify the app. Existing users can record theirs in Settings → Security. Suspending ends every session at once and keeps the account's history. Nobody can change their own access, and the last active Superadmin or Owner can't be removed.</p>`,
  )}`;
}

function admRoles() {
  const keys = Object.keys(PERMS),
    roles = Object.entries(S.roles),
    cf = sodConflicts();
  return `<div class="grid">${panel("Segregation of duties", cf.length ? `${cf.length} conflict${cf.length === 1 ? "" : "s"} in custom settings` : "No role holds conflicting rights", "", cf.length ? `<div class="inbox">${cf.map((c) => `<div><div><strong>${esc(S.roles[c.role].name)}</strong><div class="small muted">${esc(c.why)}: ${c.ps.map((p) => esc(PERMS[p])).join(" + ")}</div></div>${chip("Conflict", "bad")}</div>`).join("")}</div>` : `<p class="small muted">The Owner and Superadmin have access to every module and action. A different person must still approve a voucher or payroll they prepared.</p>`)}
   ${panel("New role", "Start from an existing role, then adjust its rights below", "", `<form id="roleform" class="form"><label>Role name<input id="nr-name" placeholder="e.g. Project Controller"></label><label>Copy rights from<select id="nr-from">${roles.map(([k, r]) => `<option value="${k}">${esc(r.name)}</option>`).join("")}</select></label><label>&nbsp;<button class="pri" type="submit">Create role</button></label>${ui.rErr ? `<p class="err wide">${esc(ui.rErr)}</p>` : ""}</form>`)}</div>
  ${panel(
    "Permission matrix",
    "Click a dot to grant or remove a right. Superadmin always has every permission. Changes apply on the user's next screen and are logged.",
    `<button class="out" data-act="roles-reset">Restore defaults</button>`,
    `<div class="tbl"><table class="matrix"><thead><tr><th>Permission</th>${roles.map(([k, r]) => `<th title="${esc(r.name)}">${esc(r.name.split(/[ /]/)[0])}</th>`).join("")}</tr></thead><tbody>
   ${keys
     .map(
       (p) =>
         `<tr><td>${esc(PERMS[p])}</td>${roles
           .map(([k, r]) => {
             const on = r.perms.includes(p),
               lk = k === "SUPER" || (k === "OWNER" && p === "admin.users");
             return `<td><button data-act="perm" data-r="${k}" data-p="${p}" aria-pressed="${on}" aria-label="${esc(r.name)}: ${esc(PERMS[p])}" ${lk ? "disabled" : ""} class="${on ? "c-good" : "c-mut"}">${on ? "●" : "○"}</button></td>`;
           })
           .join("")}</tr>`,
     )
     .join("")}
   </tbody></table></div>`,
  )}`;
}

function admCompany() {
  const co = S.company,
    qs = ["2026-03-31", "2026-06-30", "2026-09-30"];
  return `<div class="grid">${panel(
    "Registration",
    "Prints on every invoice (BIR Form 2303)",
    `<button class="out" data-act="go-reg">Edit</button>`,
    `<div class="inbox">${[
      ["Registered name", co.name],
      ["VAT Reg. TIN", co.tin],
      ["Business address", co.address],
      ["RDO", co.rdo],
      ["Fiscal year", "Calendar year, ending 31 December"],
    ]
      .map(
        (r) =>
          `<div><span class="muted">${r[0]}</span><span>${esc(r[1])}</span></div>`,
      )
      .join("")}</div>`,
  )}
   ${panel(
     "Period lock",
     `Books locked through ${fmtD(S.lockedThrough)}. Nothing posts on or before that date.`,
     "",
     `<div class="row">${
       qs
         .filter((q) => q > S.lockedThrough)
         .map(
           (q) =>
             `<button data-act="lock-set" data-id="${q}">Lock through ${fmtD(q)}</button>`,
         )
         .join("") ||
       `<span class="muted">All finished quarters are locked.</span>`
     }</div>
    <form id="reopenform" class="form"><label class="wide">Reopen to the previous quarter: reason (logged)<input id="reopen-reason" placeholder="e.g. Late supplier invoice for Q3, approved by the Owner"></label>${ui.loErr ? `<p class="err wide">${esc(ui.loErr)}</p>` : ""}<div class="row wide"><button type="submit">Reopen</button></div></form>`,
   )}</div>
  <div class="grid">${panel("Approval limits", "Large vouchers need the Owner", "", `<form id="limitform" class="form"><label>Vouchers above (₱) need the Owner's approval<input id="lim-jv" inputmode="decimal" value="${(S.settings.jvLimit / 100).toFixed(2)}" style="text-align:right"></label><label>&nbsp;<button type="submit">Save limit</button></label></form><p class="small muted">Payroll is always approved by the Finance Manager or the Owner, and never by whoever prepared it.</p>`)}
   ${panel("Invoice series", "Gapless by law; the number can't be edited", "", `<div class="inbox"><div><span class="muted">Series</span><span class="num">SI-000001 to SI-999999</span></div><div><span class="muted">Last issued</span><span class="num">${S.invoices.at(-1)?.no || "—"}</span></div><div><span class="muted">Next number</span><span class="num">SI-${String(S.seq.inv + 1).padStart(6, "0")}</span></div></div><p class="small muted">A wrong invoice is voided and kept, never deleted, so the series stays complete for BIR.</p>`)}</div>`;
}

function admSecurity() {
  const st = S.settings,
    sg = S.signins.slice(-15).reverse();
  return `<div class="grid">${panel(
    "Sign-in policy",
    "Applies to every account",
    "",
    `<form id="secform" class="form"><label style="flex-direction:row;align-items:center;gap:8px" class="wide"><input type="checkbox" id="sec-mfa" ${st.mfaRequired ? "checked" : ""} disabled style="min-width:0;height:16px;width:16px"> ${st.mfaRequired ? "Authenticator MFA Is Required For Every Account" : "Authenticator MFA Is Available"}</label>
    <label>Sign out after inactivity<select id="sec-sess">${[15, 30, 60, 240].map((m) => `<option value="${m}"${m === st.sessionMin ? " selected" : ""}>${m < 60 ? m + " minutes" : m / 60 + " hour" + (m > 60 ? "s" : "")}</option>`).join("")}</select></label>
    <label>Minimum password length<select id="sec-pw">${[12, 14, 16, 20].map((n) => `<option${n === st.pwMin ? " selected" : ""}>${n}</option>`).join("")}</select></label>
    <label class="wide">Allowed networks (optional, one per line)<textarea id="sec-ip" rows="2" placeholder="e.g. office fixed IP 203.0.113.10">${esc(st.ipAllow)}</textarea></label>
    <div class="row wide"><button class="pri" type="submit">Save policy</button>${S.users.find((u) => u.id === ui.user)?.mfa ? '<span class="small muted">Your Authenticator Is Enabled.</span><button type="button" data-act="my-mfa">Replace My Authenticator</button>' : '<button type="button" data-act="my-mfa">Set Up My Authenticator</button>'}</div></form>`,
  )}
   ${panel("Sessions", "End every session if a device is lost or a password leaks", "", `<p class="small muted">Everyone signs in again. The action is logged.</p><div class="row"><button data-act="signout-all">Sign out everyone</button></div>`)}</div>
  ${panel("Sign-in history", "Latest 15 attempts", csvBtn("t-sign"), `<div class="tbl"><table id="t-sign"><thead><tr><th>When</th><th>Account</th><th>Result</th><th>Device</th></tr></thead><tbody>${sg.map((x) => `<tr><td class="small">${when(x.t)}</td><td>${esc(x.u ? uname(x.u) : x.email)}</td><td>${x.ok ? chip("Signed in", "good") : chip(x.why || "Failed", "bad")}</td><td class="small muted">${esc(x.dev)}</td></tr>`).join("")}</tbody></table></div>`)}`;
}

function admAudit() {
  const fu = ui.audU || "",
    q = (ui.audQ || "").toLowerCase(),
    list = S.audit
      .filter(
        (a) =>
          (!fu || a.u === fu) &&
          (!q || (a.a + " " + a.d).toLowerCase().includes(q)),
      )
      .slice()
      .reverse();
  return panel(
    "Audit log",
    `${S.audit.length} entries · append-only, no role can edit or delete them`,
    csvBtn("t-audit"),
    `<div class="row"><select id="aud-u" aria-label="Filter by user"><option value="">Everyone</option>${S.users.map((u) => `<option value="${u.id}"${u.id === fu ? " selected" : ""}>${esc(u.name)}</option>`).join("")}</select><input id="aud-q" type="search" placeholder="Search actions" value="${esc(ui.audQ || "")}" aria-label="Search audit log" style="flex:0 1 260px"></div>
   <div class="tbl"><table id="t-audit"><thead><tr><th>When (Manila)</th><th>User</th><th>Action</th><th>Detail</th></tr></thead><tbody>${
     list
       .slice(0, 200)
       .map(
         (a) =>
           `<tr><td class="small">${when(a.t)}</td><td>${esc(uname(a.u))}</td><td>${esc(a.a)}</td><td>${esc(a.d)}</td></tr>`,
       )
       .join("") ||
     `<tr><td colspan="4" class="muted">No entries match.</td></tr>`
   }</tbody></table></div>`,
  );
}

function admDataContent() {
  const st = S.settings;
  return `<div class="grid">${panel("Backups", "Local backups and retained database revisions", "", `<div class="inbox"><div><span class="muted">Last backup</span><span>${when(st.backupAt)}</span></div><div><span class="muted">Retention</span><span>Books and invoices for the period NIRC §235 requires</span></div></div><div class="row"><button data-act="backup">Run backup now</button><button class="out" data-act="export">Copy all data (JSON)</button></div>`)}
   ${panel("Data privacy (RA 10173)", "Milltech is the personal information controller for staff and client contacts", "", `<div class="inbox"><div><span class="muted">Data protection officer</span><span>${esc(st.dpo)}</span></div><div><span class="muted">NPC registration</span><span>${esc(st.npc)}</span></div><div><span class="muted">Breach notice</span><span>NPC and affected people within 72 hours (NPC Circular 16-03)</span></div></div><p class="small muted">Salaries, TIN and SSS numbers are masked outside HR, payroll and approvers. Exports carry personal data; share them only with the accountant or auditor.</p>`)}</div>
  ${panel("Reset demo data", "Restores the sample company and clears every change made in this browser", "", ui.resetAsk ? `<div class="row"><span class="err">This can't be undone.</span><button class="pri" data-act="reset">Yes, reset everything</button><button class="ghost" data-act="reset-no">Keep my data</button></div>` : `<div class="row"><button data-act="reset-ask">Reset demo data</button></div>`)}`;
}

export {
  ADM_TABS,
  ADM_SUB,
  SOD,
  EMAIL_RE,
  activeOf,
  guardChange,
  sodConflicts,
  adminAlerts,
  admOverview,
  admUsers,
  admRoles,
  admCompany,
  admSecurity,
  admAudit,
  admData,
};

function admData() {
  return admDataContent() + workspaceTools();
}
